import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Workspace detail"` — P11-08: people, invitations and roles.
 *
 * **FOUR ROLES.** Owner / Admin / Member / Viewer. Product decision Q10 settled it and the
 * plan's 2026-09-22 amendment is explicit that the invite select offers the same four — the
 * design file's three-role select is the contradiction Q10 resolved, and a three-role form
 * here would be a surface that cannot express a state the database holds.
 *
 * AMENDED 2026-09-25 (customer-portal review #132, #134, #135, #149): the People table is
 * `PeoplePanel`, shared with Workspaces & team, and offers each role only the controls it can
 * use — an Admin is never offered an Owner's role or Remove, a Member or a Viewer sees the list
 * read-only. The server still refuses what it refuses (the last Owner, an Admin touching an
 * Owner) and the panel reports it. "Workspace settings" is shown only on the current workspace:
 * Settings edits the current workspace, so on any other page it would change the wrong one.
 *
 * AMENDED 2026-09-25 (package M — review #150, #152, #153, #161, #162):
 *   · a read that fails says so, with the request id and Try again; a workspace that does not
 *     exist (or is not yours, or was deleted) says exactly that, with the way back — never an
 *     endless "Reading this workspace…";
 *   · Owners and Admins rename the workspace here (name and time zone; the slug never changes);
 *     an Owner deletes an EMPTY workspace that is not the one they are working in — the button
 *     says why when it cannot, and asks first when it can;
 *   · each agent row opens its agent and says when it was made, so two with one name can be told
 *     apart; live and failed agents are counted apart.
 *
 * AMENDED 2026-10-06 (plan-014 B3; design §27.15.1 "funding per workspace; People tab invite with six roles";
 * FR12-WSP-02, FR12-TEAM-05):
 *   · **Paid by** — the balance that pays for the workspace now (the shared balance or its own) and since when, with
 *     the way to change it (Finance → Funding) for whoever may; the workspace's **monthly cap**, changed on Billing
 *     (AMENDED 2026-10-06, plan-014 E3b: changed here — cap, behaviour at the cap, warning — by the workspace's Owners
 *     and Admins, through E3a's spend cap route);
 *   · **How people join** — invitation only, or through one of the organization's verified domains in a constrained
 *     role (never Owner or Admin), changed by the workspace's Owner or Admin or the organization's;
 *   · the People panel offers what the person's role IN THIS workspace allows (the API judges the same target), with
 *     all seven roles and the expired and revoked invitations apart.
 *
 * AMENDED 2026-10-09 (ADR 0041, the owner's decision of 2026-10-09): the organization's Owner and Admin see the people
 * of, and are offered Delete workspace and Archive on, every workspace of the organization — member there or not.
 * Delete and Archive are judged against THIS workspace (the role held here, `management.memberRole`, and the
 * organization role), never the role in whichever workspace is open; "Delete workspace" counts the OTHER people here
 * (everyone but the signed-in person), so an organization Owner who is not a member sees the true count.
 *
 * AMENDED 2026-10-09 (plan-015 WSK, G25, ADR 0050; owner answers 15, 33): a Test workspace carries the tag "Test" beside
 * its state, and the facts gain **What it is for** — Standard or Test. Only the organization's Owner or Admin changes it
 * (the rename's capability, `account.workspace.manage`, judged again by the API), and the API audits every change
 * `workspace.kind_changed`. A label only: the figures, the funding and the cap above are any workspace's.
 *
 * AMENDED 2026-10-10 (ADR 0052): the People panel offers the organization's Owner and Admin the role boxes, Remove and
 * Revoke on every workspace. And "Delete workspace", greyed while the workspace still has agents or other people, says
 * why in a visible note under the actions — the reason used to live only in a tooltip on a disabled button, which most
 * browsers never show.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ApiError } from "@wetalk/api-client";
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Segmented,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { ArchivePreflightDialog } from "./archive-preflight.js"; // plan-014 B4

import { useGateway } from "../../api/provider.js";
import type {
  WorkspaceAccessPolicyView,
  WorkspaceJoinMode,
  WorkspaceKind,
  WorkspaceManagementView,
} from "../../api/slice/workspace-management.js";
import { QUERY_KEY, authSessionQuery, workspaceDetailQuery, workspacePeopleQuery } from "../../api/query.js";
import { apiProblemText } from "../../api/api-error.js";
import type { WorkspaceDetailView } from "../../api/gateway.js";
import { formatMoney } from "../../money.js";
import { talkTimeText } from "../../talk-time.js";
import { GLYPH } from "../../shell/glyph.js";
import { consoleCan, useMemberCan } from "../../shell/member-capability.js";
import { useSwitchWorkspace } from "../../shell/workspace-switch.js";
import { WorkspaceSpendCap } from "../finance/allowances/workspace-spend-cap.js";
import { zoneChoice } from "./offered-zone.js";
import { PeoplePanel, ROLE, roleLabel } from "./people-panel.js";
import { isConstrainedJoinRoleView } from "./join-rule.js";
import { voiceAgentStateLabel, voiceAgentTally } from "./voice-agent-tally.js";
import { WORKSPACE_NAME_MAXIMUM, nameLengthOf } from "./workspace-name.js";
import "./workspaces.css";

export interface WorkspaceDetailScreenProps {
  readonly workspaceId: string;
}

export function WorkspaceDetailScreen({ workspaceId }: WorkspaceDetailScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const detail = useQuery(workspaceDetailQuery(gateway, workspaceId));
  const management = useQuery({
    queryKey: [...QUERY_KEY.workspace, workspaceId, "management"],
    queryFn: () => gateway.readWorkspaceManagement(workspaceId),
  });
  const authSession = useQuery(authSessionQuery(gateway));
  const isCurrent = authSession.data?.workspaceId === workspaceId;
  const workspaceSwitch = useSwitchWorkspace(authSession.data?.accountId ?? null);
  const member = useMemberCan();
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // plan-014 B4: the archive preflight dialog, and what it answered.
  const [archiving, setArchiving] = useState(false);
  const [archived, setArchived] = usePortalMessage();
  // ADR 0041: judged against THIS workspace — the role held here, and the organization's.
  const mayRetire = consoleCan({ memberRole: management.data?.memberRole ?? null, accountRole: member.accountRole }, "workspace.delete");
  const deleteRefusal = useDeleteRefusal(detail.data, workspaceId, mayRetire && !isCurrent);

  if (detail.data === undefined) {
    if (!detail.isError) return <p className="c-note c-page">{portalText("core", "workspaces.reading_this_workspace_7e4c61")}</p>;
    const missing = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <section className="c-page">
        <Link to="/organization/workspaces" className="c-backlink">
          <Icon glyph={GLYPH.arrowLeft} size="sm" />{portalText("core", "workspaces.all_workspaces_22e3fb")}</Link>
        {missing ? (
          <p className="c-note c-note-critical" role="alert">{portalText("core", "workspaces.this_workspace_does_not_exist_or_it_is_not_in_your_account_563653")}</p>
        ) : (
          <p className="c-note c-note-critical" role="alert">{portalText("core", "workspaces.this_workspace_could_not_be_read_10f656")}{" "}{apiProblemText(detail.error, "")}{" "}
            <Button
              variant="ghost"
              onClick={() => {
                void detail.refetch();
              }}
            >{portalText("core", "workspaces.try_again_042c86")}</Button>
          </p>
        )}
      </section>
    );
  }
  const one = detail.data;
  const tally = voiceAgentTally(one.voiceAgent);

  return (
    <section className="c-page c-ws-detail">
      <Link to="/organization/workspaces" className="c-backlink">
        <Icon glyph={GLYPH.arrowLeft} size="sm" />{portalText("core", "workspaces.all_workspaces_22e3fb")}</Link>

      <div className="c-headline-row c-ws-detail-head">
        <div className="c-row c-ws-detail-title">
          <span className="c-mark c-mark-lg">{one.initials}</span>
          <div className="c-ws-detail-name">
            <h1 title={one.name}>{one.name}</h1>
            {/* AMENDED 2026-10-15 (design audit): the design's line — "3 people · 2 agents · created 12 June 2026". */}
            <p className="c-kicker">
              {portalText("core", "workspaces.people_count", { count: one.memberCount })} ·{" "}
              {portalText("core", "workspaces.agents_count", { count: tally.total })}{" "}
              {portalText("core", "workspaces._created_bcdf32")}{" "}{one.createdAtText}
            </p>
          </div>
          {/* The design's tag: "Current" on the workspace open now, else its lifecycle. */}
          {isCurrent ? (
            <Tag tone="accent">{portalText("core", "dashboard.current_4fc0e2")}</Tag>
          ) : (
            <Tag status={one.state === "active" ? "positive" : "muted"}>
              {one.state === "active" ? portalText("core", "workspaces.active_a733b8") : portalText("core", "account_admin.archived_eddc81")}
            </Tag>
          )}
          {management.data?.kind === "test" ? <Tag status="muted">{portalText("core", "workspaces.test_640ab2")}</Tag> : null}
        </div>
        <div className="c-row c-ws-detail-actions">
          {/* The design's primary action on a workspace that is not the one open now (`wsDetail.pick`). */}
          {isCurrent || one.state !== "active" || (management.data?.memberRole ?? null) === null ? null : (
            <Button
              variant="primary"
              disabled={workspaceSwitch.pending}
              onClick={() => {
                workspaceSwitch.switchTo(workspaceId);
              }}
            >{portalText("core", "workspaces.switch_to_this_workspace_c3b50c")}</Button>
          )}
          {member.can("workspace.create") ? (
            <Button
              variant="secondary"
              onClick={() => {
                setRenaming(true);
              }}
            >{portalText("core", "workspaces.rename_d3f4cb")}</Button>
          ) : null}
          {isCurrent ? (
            <Link to="/settings" className="btn btn-secondary">{portalText("core", "workspaces.workspace_settings_432fa1")}</Link>
          ) : mayRetire ? (
            <Button
              variant="secondary"
              disabled={deleteRefusal !== null}
              title={deleteRefusal === null || deleteRefusal === DELETE_REFUSAL_UNKNOWN ? undefined : deleteRefusal}
              onClick={() => {
                setDeleting(true);
              }}
            >{portalText("core", "workspaces.delete_workspace_bc2cf9")}</Button>
          ) : null}
          {/* plan-014 B4: archive with the item-by-item preflight (FR12-WSP-03); restore is on Organization → Archive and close. */}
          {mayRetire && one.state === "active" ? (
            <Button
              variant="secondary"
              onClick={() => {
                setArchiving(true);
              }}
            >{portalText("core", "workspaces.archive_2621c6")}</Button>
          ) : null}
        </div>
      </div>
      {!isCurrent && mayRetire && deleteRefusal !== null && deleteRefusal !== DELETE_REFUSAL_UNKNOWN ? (
        <p className="c-note">{deleteRefusal}</p>
      ) : null}
      {archived === null ? null : (
        <p className="c-note" role="status">
          {archived}
        </p>
      )}

      <div className="c-grid c-grid-figures">
        <div className="c-figure">
          <span className="c-figure-value">{String(tally.answering)}</span>
          <span className="c-figure-label">{portalText("core", "workspaces.agents_live_7bd770")}{" "}{String(tally.total)}{" "}{portalText("core", "workspaces.in_all_8f6445")}{tally.failed > 0 ? portalText("core", "workspaces._failed_918196", { value0: String(tally.failed) }) : ""}
          </span>
        </div>
        <div className="c-figure">
          <span className="c-figure-value">{String(one.memberCount)}</span>
          <span className="c-figure-label">{one.memberCount === 1 ? portalText("core", "account_admin.person_8c41ae") : portalText("core", "account_admin.people_b37554")}</span>
        </div>
        <div className="c-figure">
          <span className="c-figure-value">{talkTimeText(one.monthTalkSecond)}</span>
          <span className="c-figure-label">{portalText("core", "workspaces.minutes_this_month_a6d768")}</span>
        </div>
        <div className="c-figure">
          <span className="c-figure-value">{formatMoney(one.monthSpend)}</span>
          <span className="c-figure-label">{portalText("core", "workspaces.spend_this_month_585649")}</span>
        </div>
      </div>

      {/* --- funding and joining (plan-014 B3) ------------------------------------------------ */}
      {management.data === undefined ? (
        management.isError ? (
          <p className="c-note c-note-critical" role="alert">{portalText("core", "workspaces.how_this_workspace_is_paid_for_could_not_be_read_470f60")}{" "}{apiProblemText(management.error, "")}
          </p>
        ) : null
      ) : (
        <div className="c-ws-facts">
          <FundingFact one={management.data} isCurrent={isCurrent} maySpend={member.can("billing.write")} />
          <JoinRuleFact workspaceId={workspaceId} />
          <KindFact one={management.data} mayChange={member.can("workspace.rename")} />
        </div>
      )}

      {/* --- people ------------------------------------------------------------------- */}
      <PeoplePanel workspaceId={workspaceId} workspaceName={one.name} callerRole={management.data?.memberRole ?? null} />


      {/* --- agents in this workspace --------------------------------------------------- */}
      <p className="c-section-title">{portalText("core", "workspaces.agents_in_this_workspace_5c2851")}</p>
      {one.voiceAgent.length === 0 ? (
        <p className="c-note">{portalText("core", "workspaces.no_agents_here_yet_cf316a")}</p>
      ) : (
        <div className="c-people-scroll c-stack-table">
          <Table caption={portalText("core", "workspaces.agents_in_this_workspace_with_this_month_s_minutes_and_spend_3bed24")}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("core", "dashboard.agent_5ce2e6")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "dashboard.template_3ec1ae")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "dashboard.status_bae7d5")}</TableHeaderCell>
                <TableHeaderCell numeric>{portalText("core", "workspaces.minutes_092f99")}</TableHeaderCell>
                <TableHeaderCell numeric>{portalText("core", "workspaces.spend_14eeee")}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {one.voiceAgent.map((each) => (
                <TableRow key={each.voiceAgentId}>
                  <TableCell>
                    {isCurrent ? (
                      <Link
                        to="/agents/$voiceAgentId"
                        params={{ voiceAgentId: each.voiceAgentId }}
                        className="c-ws-agent-link"
                      >
                        {each.displayName}
                      </Link>
                    ) : (
                      <span className="c-ws-agent-link">{each.displayName}</span>
                    )}
                    <span className="c-ws-agent-made">{portalText("core", "workspaces.created_21c508")}{" "}{each.createdAtText}</span>
                  </TableCell>
                  <TableCell>{each.agentTemplateName ?? "—"}</TableCell>
                  <TableCell>
                    <Tag status={each.state === "live" ? "positive" : each.state === "failed" ? "critical" : "muted"}>
                      {voiceAgentStateLabel(each.state)}
                    </Tag>
                  </TableCell>
                  <TableCell numeric>{talkTimeText(each.monthTalkSecond)}</TableCell>
                  <TableCell numeric>{formatMoney(each.monthSpend)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isCurrent ? null : (
            <p className="c-note">{portalText("core", "workspaces.switch_to_this_workspace_to_open_its_agents_68f90f")}</p>
          )}
        </div>
      )}

      {/* The roles line sits at the foot of the page, where the design puts it. */}
      <p className="c-empty-dek">{portalText("core", "workspaces.owners_can_archive_the_workspace_admins_manage_agents_channels_pe_2be425")}</p>

      <RenameDialog
        open={renaming}
        workspace={one}
        onClose={() => {
          setRenaming(false);
        }}
      />
      <DeleteDialog
        open={deleting}
        workspace={one}
        onClose={() => {
          setDeleting(false);
        }}
      />
      {archiving ? (
        <ArchivePreflightDialog
          open={archiving}
          workspaceId={one.workspaceId}
          onClose={() => {
            setArchiving(false);
          }}
          onDone={setArchived}
        />
      ) : null}
    </section>
  );
}

/** The people are still being read: "Delete workspace" waits, with nothing to explain yet. */
const DELETE_REFUSAL_UNKNOWN = "";

/**
 * Why "Delete workspace" is greyed, or `null` when it may be asked for — offered to an Owner on a workspace they are
 * not working in (and, ADR 0041, to the organization's Owner and Admin on any workspace they are not working in). Only
 * an empty workspace can be deleted: no agents, and nobody but the signed-in person, member here or not.
 * {@link DELETE_REFUSAL_UNKNOWN} while the people are read. AMENDED 2026-10-10: the screen shows the reason as a note
 * under the actions, not only as a tooltip on the disabled button.
 */
function useDeleteRefusal(workspace: WorkspaceDetailView | undefined, workspaceId: string, offered: boolean): string | null {
  usePortalTranslation("core");
  const gateway = useGateway();
  // Read only where "Delete workspace" is offered: a role that may not read the people asks nothing.
  const people = useQuery({ ...workspacePeopleQuery(gateway, workspaceId), enabled: offered });
  const signedInPersonId = useQuery(authSessionQuery(gateway)).data?.person.personId ?? null;
  if (workspace !== undefined && workspace.voiceAgentCount > 0) {
    return portalText("core", "workspaces.only_an_empty_workspace_can_be_deleted_this_one_still_has_agents_0969ec");
  }
  if (people.data === undefined) return DELETE_REFUSAL_UNKNOWN;
  const others = people.data.member.filter((one) => one.personId !== signedInPersonId).length;
  return others > 0 ? portalText("core", "workspaces.only_an_empty_workspace_can_be_deleted_remove_the_other_people_in_5c9b04") : null;
}

function DeleteDialog({
  open,
  workspace,
  onClose,
}: {
  readonly open: boolean;
  readonly workspace: WorkspaceDetailView;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [problem, setProblem] = usePortalMessage();
  const remove = useMutation({
    mutationFn: () => gateway.archiveWorkspace(workspace.workspaceId),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
      await navigate({ to: "/organization/workspaces" });
    },
    onError: (error: unknown) => {
      setProblem(() => (apiProblemText(error, portalText("core", "workspaces.that_workspace_could_not_be_deleted_4481b5"))));
    },
  });
  return (
    <Dialog
      open={open}
      title={portalText("core", "workspaces.delete_9b216b", { value0: workspace.name })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("core", "workspaces.keep_it_d9f369")}</Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() => {
              setProblem(null);
              remove.mutate();
            }}
          >{portalText("core", "workspaces.delete_workspace_bc2cf9")}</Button>
        </>
      }
    >
      <p className="c-note">
        {workspace.name}{" "}{portalText("core", "workspaces.disappears_from_every_workspace_list_and_any_invitation_to_it_sto_0a9d24")}</p>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
    </Dialog>
  );
}

/** Rename a workspace or change its time zone. The slug, used in storage paths, never changes. */
function RenameDialog({
  open,
  workspace,
  onClose,
}: {
  readonly open: boolean;
  readonly workspace: WorkspaceDetailView;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [name, setName] = useState(workspace.name);
  const [timeZone, setTimeZone] = useState(workspace.timeZone);
  const [problem, setProblem] = usePortalMessage();
  const nameLength = nameLengthOf(name);
  const nameTooLong = nameLength > WORKSPACE_NAME_MAXIMUM;

  const save = useMutation({
    mutationFn: () => gateway.renameWorkspace(workspace.workspaceId, { name: name.trim(), timeZone }),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
    },
    onError: (error: unknown) => {
      setProblem(() => (apiProblemText(error, portalText("core", "workspaces.that_workspace_could_not_be_renamed_7bf86e"))));
    },
  });

  return (
    <Dialog
      open={open}
      title={portalText("core", "workspaces.rename_workspace_6d7e95")}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("core", "workspaces.cancel_77dfd2")}</Button>
          <Button
            variant="primary"
            disabled={nameLength === 0 || nameTooLong || save.isPending}
            onClick={() => {
              setProblem(null);
              save.mutate();
            }}
          >{portalText("core", "account_admin.save_efc007")}</Button>
        </>
      }
    >
      <Field
        label={portalText("core", "workspaces.name_709a23")}
        description={portalText("core", "workspaces._of_characters_381b09", { value0: String(nameLength), value1: String(WORKSPACE_NAME_MAXIMUM) })}
        {...(nameTooLong ? { error: portalText("core", "workspaces.a_workspace_name_can_be_at_most_characters_a6bb31", { value0: String(WORKSPACE_NAME_MAXIMUM) }) } : {})}
      >
        {(control) => (
          <Input
            {...control}
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      <Field label={portalText("core", "auth.time_zone_eea79a")} description={portalText("core", "workspaces.opening_hours_and_the_billing_day_are_both_taken_in_this_zone_429454")}>
        {(control) => (
          <Select
            {...control}
            value={timeZone}
            onChange={(event) => {
              setTimeZone(event.currentTarget.value);
            }}
          >
            {zoneChoice(workspace.timeZone).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
    </Dialog>
  );
}

/**
 * "Paid by" and the monthly cap (plan-014 B3): which balance pays now, since when, and where it is changed. plan-014 E3b:
 * the cap is changed here by whoever holds `spend_cap.write` in this workspace (`WorkspaceSpendCap`).
 */
function FundingFact({
  one,
  isCurrent,
  maySpend,
}: {
  readonly one: WorkspaceManagementView;
  readonly isCurrent: boolean;
  readonly maySpend: boolean;
}): ReactNode {
  usePortalTranslation("core");
  const balance = one.funding.kind === "dedicated" ? (one.funding.billingAccountName ?? portalText("core", "workspaces.its_own_balance_296394")) : portalText("core", "workspaces.the_organization_s_shared_balance_80564f");
  return (
    <div className="c-ws-fact">
      <p className="c-ws-fact-label">{portalText("core", "workspaces.paid_by_70c115")}</p>
      <p className="c-ws-fact-value">
        {balance}
        {one.funding.sinceText === null ? "" : portalText("core", "workspaces._ce415a", { value0: one.funding.sinceText })}
      </p>
      <WorkspaceSpendCap workspaceId={one.workspaceId} workspaceName={one.name} memberRole={one.memberRole} cap={one.spendCap} isCurrent={isCurrent} />
      {maySpend ? (
        <Link to="/finance/funding" className="c-ws-fact-link">{portalText("core", "workspaces.change_how_it_is_funded_91334e")}</Link>
      ) : null}
    </div>
  );
}

/** "Standard" or "Test or staging" — the words "What it is for" uses. */
function kindWordOf(kind: WorkspaceKind): string {
  return kind === "test" ? portalText("core", "workspaces.test_or_staging_651289") : portalText("core", "workspaces.standard_2dfa66");
}

/**
 * "What it is for" (plan-015 WSK, G25): Standard or Test — a label, billed like any workspace. The organization's Owner
 * or Admin changes it here (If-Match: the revision this page read); everyone else reads it.
 */
function KindFact({ one, mayChange }: { readonly one: WorkspaceManagementView; readonly mayChange: boolean }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [problem, setProblem] = usePortalMessage();
  const [saved, setSaved] = usePortalMessage();
  const change = useMutation({
    mutationFn: (kind: WorkspaceKind) =>
      gateway.changeWorkspaceKind(one.workspaceId, { name: one.name, timeZone: one.timeZone, kind, revision: one.revision }),
    onSuccess: async (changed) => {
      setProblem(null);
      setSaved(() => (changed.kind === "test" ? portalText("core", "workspaces.saved_marked_test_on_the_workspace_list_a74ea4") : portalText("core", "workspaces.saved_no_longer_marked_test_5bc01a")));
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
    },
    onError: (error: unknown) => {
      setSaved(null);
      setProblem(() => (apiProblemText(error, portalText("core", "workspaces.what_this_workspace_is_for_could_not_be_saved_dbb4dd"))));
    },
  });
  return (
    <div className="c-ws-fact">
      <p className="c-ws-fact-label">{portalText("core", "workspaces.what_it_is_for_9d49f2")}</p>
      {mayChange && one.state === "active" ? (
        <Segmented<WorkspaceKind>
          name="workspace-kind"
          legend={portalText("core", "workspaces.what_it_is_for_9d49f2")}
          options={[
            { value: "standard", label: kindWordOf("standard") },
            { value: "test", label: kindWordOf("test") },
          ]}
          value={one.kind}
          onChange={(next) => {
            if (next !== one.kind && !change.isPending) change.mutate(next);
          }}
        />
      ) : (
        <p className="c-ws-fact-value">{kindWordOf(one.kind)}</p>
      )}
      <p className="c-ws-fact-dek">{portalText("core", "workspaces.a_test_workspace_is_marked_test_on_the_workspace_list_it_is_bille_2d7316")}</p>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
      {saved === null ? null : (
        <p className="c-note" role="status">
          {saved}
        </p>
      )}
    </div>
  );
}

const JOIN_MODE_WORD: Readonly<Record<WorkspaceJoinMode, string>> = {
  get invitation_only() { return portalText("core", "workspaces.by_invitation_only_9b6738"); },
  get verified_domain() { return portalText("core", "workspaces.through_a_verified_domain_4f95bf"); },
};

/** "How people join" (plan-014 B3): the rule, and — for whoever may — the way to change it. */
function JoinRuleFact({ workspaceId }: { readonly workspaceId: string }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const policy = useQuery({
    queryKey: [...QUERY_KEY.workspace, workspaceId, "access-policy"],
    queryFn: () => gateway.readWorkspaceAccessPolicy(workspaceId),
  });
  const [editing, setEditing] = useState(false);
  if (policy.data === undefined) {
    return policy.isError ? null : (
      <div className="c-ws-fact">
        <p className="c-ws-fact-label">{portalText("core", "workspaces.how_people_join_0eca56")}</p>
        <p className="c-note">{portalText("core", "workspaces.reading_ae18b7")}</p>
      </div>
    );
  }
  const rule = policy.data;
  return (
    <div className="c-ws-fact">
      <p className="c-ws-fact-label">{portalText("core", "workspaces.how_people_join_0eca56")}</p>
      <p className="c-ws-fact-value">{JOIN_MODE_WORD[rule.joinMode]}</p>
      <p className="c-ws-fact-dek">
        {rule.joinMode === "verified_domain" && rule.domainJoinRole !== null
          ? portalText("core", "workspaces.anyone_signing_in_with_an_address_on_joins_as_3bddf8", { value0: rule.domain ?? portalText("core", "account_admin.the_domain_c72917"), value1: roleLabel(rule.domainJoinRole) })
          : portalText("core", "workspaces.people_join_only_when_someone_here_invites_them_25a092")}
      </p>
      {rule.verifiedDomain === null ? null : (
        <Button
          variant="ghost"
          onClick={() => {
            setEditing(true);
          }}
        >{portalText("core", "workspaces.change_how_people_join_75af61")}</Button>
      )}
      {/* Mounted only while open, so it starts from the rule as it stands each time. */}
      {rule.verifiedDomain === null || !editing ? null : (
        <JoinRuleDialog
          open={editing}
          workspaceId={workspaceId}
          rule={rule}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function JoinRuleDialog({
  open,
  workspaceId,
  rule,
  onClose,
}: {
  readonly open: boolean;
  readonly workspaceId: string;
  readonly rule: WorkspaceAccessPolicyView;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const domains = rule.verifiedDomain ?? [];
  const [mode, setMode] = useState<WorkspaceJoinMode>(rule.joinMode);
  const [domainId, setDomainId] = useState(rule.ssoDomainId ?? domains[0]?.ssoDomainId ?? "");
  const [role, setRole] = useState(rule.domainJoinRole ?? "viewer");
  const [problem, setProblem] = usePortalMessage();
  const save = useMutation({
    mutationFn: () =>
      gateway.changeWorkspaceAccessPolicy(
        workspaceId,
        mode === "verified_domain" ? { joinMode: mode, domainJoinRole: role, ssoDomainId: domainId } : { joinMode: mode, domainJoinRole: null, ssoDomainId: null },
        rule.revision,
      ),
    onSuccess: async () => {
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.workspace, workspaceId, "access-policy"] });
      onClose();
    },
    onError: (error: unknown) => {
      setProblem(() => (apiProblemText(error, portalText("core", "workspaces.the_join_rules_could_not_be_saved_21aa88"))));
    },
  });
  const blocked = mode === "verified_domain" && (domainId === "" || domains.length === 0);
  return (
    <Dialog
      open={open}
      title={portalText("core", "workspaces.how_people_join_0eca56")}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("core", "workspaces.cancel_77dfd2")}</Button>
          <Button
            variant="primary"
            disabled={blocked || save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >{portalText("core", "account_admin.save_efc007")}</Button>
        </>
      }
    >
      <Segmented<WorkspaceJoinMode>
        name="workspace-join-mode"
        legend={portalText("core", "workspaces.how_people_join_0eca56")}
        options={[
          { value: "invitation_only", label: JOIN_MODE_WORD.invitation_only },
          { value: "verified_domain", label: JOIN_MODE_WORD.verified_domain },
        ]}
        value={mode}
        onChange={setMode}
      />
      {mode === "verified_domain" ? (
        domains.length === 0 ? (
          <p className="c-note">{portalText("core", "workspaces.this_organization_has_no_verified_domain_yet_verify_one_under_org_8220e7")}</p>
        ) : (
          <>
            <Field label={portalText("core", "account_admin.domain_9b1091")} description={portalText("core", "workspaces.only_a_domain_this_organization_has_verified_c4abd2")}>
              {(control) => (
                <Select
                  {...control}
                  value={domainId}
                  onChange={(event) => {
                    setDomainId(event.currentTarget.value);
                  }}
                >
                  {domains.map((one) => (
                    <option key={one.ssoDomainId} value={one.ssoDomainId}>
                      {one.domain}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={portalText("core", "account_admin.they_join_as_3c0da5")} description={portalText("core", "workspaces.never_owner_or_admin_those_are_given_by_a_person_by_invitation_c380e3")}>
              {(control) => (
                <Select
                  {...control}
                  value={role}
                  onChange={(event) => {
                    const next = ROLE.find((one) => one.value === event.currentTarget.value);
                    if (next !== undefined) setRole(next.value);
                  }}
                >
                  {ROLE.filter((one) => isConstrainedJoinRoleView(one.value)).map((one) => (
                    <option key={one.value} value={one.value}>
                      {one.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </>
        )
      ) : (
        <p className="c-note">{portalText("core", "workspaces.nobody_joins_without_an_invitation_from_an_owner_or_admin_of_this_e31234")}</p>
      )}
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
    </Dialog>
  );
}
